
import { useEffect, useState } from 'react';
import '../../components/market/MarketIntelligencePremium.css';
import HeroBanner from '../../components/market/HeroBanner';
import MacroMetricsGrid from '../../components/market/MacroMetricsGrid';
import MarketAISummary from '../../components/market/MarketAISummary';
import { getMarketRegimeLabel, getMarketRiskLabel } from '../../components/market/marketRegimeLabels';

const formatPercent = (value, digits = 1) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return 'N/A';
  }

  return `${numericValue.toFixed(digits)}%`;
};

const formatUsdCompact = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return 'N/A';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(numericValue);
};

const formatScore = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return 'N/A';
  }

  const normalizedValue = Math.abs(numericValue) <= 1 ? numericValue * 100 : numericValue;
  return `${Math.round(normalizedValue)}/100`;
};

const deriveFallbackRegime = (metrics, sentiment) => {
  const rawRegime = String(metrics?.regime || metrics?.marketRegime || '').toUpperCase();
  if (rawRegime) {
    return rawRegime;
  }

  const btcDominance = Number(metrics?.btcDominance);
  const ethDominance = Number(metrics?.ethDominance);

  if (Number.isFinite(btcDominance)) {
    if (btcDominance > 55) {
      return 'BTC_DOMINANCE_UP';
    }
    if (btcDominance < 45) {
      return 'ALTSEASON';
    }
  }

  if (Number.isFinite(ethDominance) && ethDominance > 18) {
    return 'ETH_ROTATION';
  }

  if (Number.isFinite(Number(sentiment))) {
    return Number(sentiment) < 40 ? 'DEFENSIVE' : 'NEUTRAL';
  }

  return 'NEUTRAL';
};

const deriveFallbackVolatility = (metrics) => {
  const rawVolatility = Number(metrics?.volatility);
  if (Number.isFinite(rawVolatility)) {
    return rawVolatility;
  }

  const marketMove24h = Math.abs(Number(metrics?.marketCap24hChange));
  if (!Number.isFinite(marketMove24h)) {
    return null;
  }

  // Proxy volatility from daily market-cap swing when backend volatility is missing.
  return Number((marketMove24h * 1.35).toFixed(2));
};

const deriveFallbackRiskOnOff = (metrics, sentiment, regime) => {
  const currentRisk = String(metrics?.riskOnOff || '').trim();
  if (currentRisk) {
    return currentRisk;
  }

  const marketMove24h = Number(metrics?.marketCap24hChange);
  const normalizedRegime = String(regime || '').toUpperCase();
  const normalizedSentiment = Number(sentiment);

  const hasDefensiveSignals =
    (Number.isFinite(normalizedSentiment) && normalizedSentiment <= 35)
    || normalizedRegime.includes('DEFENS')
    || (Number.isFinite(marketMove24h) && marketMove24h <= -2);

  if (hasDefensiveSignals) {
    return 'RISK_OFF';
  }

  const hasOffensiveSignals =
    (Number.isFinite(normalizedSentiment) && normalizedSentiment >= 60)
    || normalizedRegime.includes('ALT')
    || (Number.isFinite(marketMove24h) && marketMove24h >= 1.5);

  if (hasOffensiveSignals) {
    return 'RISK_ON';
  }

  return 'NEUTRAL';
};

const mergeMarketMetrics = (marketMetrics, fearAndGreedPayload) => {
  const liveSentiment = marketMetrics?.sentiment ?? marketMetrics?.sentimentScore;
  const liveSentimentValue = Number(liveSentiment);
  const fearAndGreedValue = Number(fearAndGreedPayload?.data?.[0]?.value);
  const sentiment = Number.isFinite(liveSentimentValue)
    ? liveSentimentValue
    : (Number.isFinite(fearAndGreedValue) ? fearAndGreedValue : null);

  const regime = deriveFallbackRegime(marketMetrics, sentiment);
  const volatility = deriveFallbackVolatility(marketMetrics);
  const riskOnOff = deriveFallbackRiskOnOff(marketMetrics, sentiment, regime);

  return {
    ...marketMetrics,
    sentiment,
    sentimentScore: sentiment,
    regime,
    volatility,
    riskOnOff,
    macroScore: Number.isFinite(Number(marketMetrics?.macroScore))
      ? Number(marketMetrics.macroScore)
      : (Number.isFinite(sentiment) ? sentiment / 100 : null),
  };
};

const getSentimentLabel = (sentiment) => {
  if (sentiment == null || Number.isNaN(Number(sentiment))) {
    return 'N/A';
  }

  if (sentiment < 25) return 'Peur extrême';
  if (sentiment < 45) return 'Peur';
  if (sentiment < 60) return 'Neutre';
  if (sentiment < 75) return 'Optimisme';
  return 'Avidité';
};

const MarketIntelligencePage = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setError('');

      try {
        const [marketResponse, fearAndGreedResponse] = await Promise.all([
          fetch('/api/market/metrics'),
          fetch('/api/fear-and-greed/index'),
        ]);

        if (!marketResponse.ok) {
          throw new Error('Impossible de récupérer les métriques marché.');
        }

        const marketData = await marketResponse.json();
        const fearAndGreedData = fearAndGreedResponse.ok ? await fearAndGreedResponse.json() : null;
        setMetrics(mergeMarketMetrics(marketData, fearAndGreedData));
      } catch (fetchError) {
        setMetrics(null);
        setError(fetchError instanceof Error ? fetchError.message : 'Impossible de récupérer les métriques marché.');
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  if (loading) return <div>Chargement des données marché…</div>;
  if (!metrics) {
    return <div>{error || 'Données marché indisponibles.'}</div>;
  }

  const sentimentLabel = getSentimentLabel(metrics.sentiment);
  const displayRegimeLabel = getMarketRegimeLabel(metrics.regime);
  const displayRiskLabel = metrics.riskOnOff ? getMarketRiskLabel(metrics.riskOnOff) : 'N/A';
  const watchItems = [
    {
      label: 'Régime détecté',
      value: displayRegimeLabel,
      helper: metrics.riskOnOff ? displayRiskLabel : 'Qualification automatique du terrain',
    },
    {
      label: 'Sentiment',
      value: sentimentLabel,
      helper: metrics.sentiment != null ? `${Math.round(Number(metrics.sentiment))}/100` : 'N/A',
    },
    {
      label: 'Dominance BTC',
      value: formatPercent(metrics.btcDominance, 1),
      helper: `ETH ${formatPercent(metrics.ethDominance, 1)}`,
    },
    {
      label: 'Capitalisation',
      value: formatUsdCompact(metrics.marketCap),
      helper: `Variation 24h ${formatPercent(metrics.marketCap24hChange, 2)}`,
    },
  ];
  const metaChips = [
    `Régime ${displayRegimeLabel}`,
    `Sentiment ${metrics.sentiment != null ? `${Math.round(Number(metrics.sentiment))}/100` : 'N/A'}`,
    `BTC ${formatPercent(metrics.btcDominance, 1)}`,
    `Mise à jour ${metrics.lastUpdate || metrics.timestamp ? new Date(metrics.lastUpdate || metrics.timestamp).toLocaleString('fr-FR') : 'N/A'}`,
  ];

  return (
    <div className="market-intelligence-premium">
      <div className="market-page-shell">
        <section className="market-page-intro">
          <div className="market-page-kicker">Cockpit macro</div>
          <div className="market-page-heading-row">
            <div>
              <h1>Lecture marché</h1>
              <p>
                Lis ici le terrain avant toute décision: régime, dominance, sentiment et qualité du flux.
              </p>
              <div className="market-meta-row">
                {metaChips.map((chip) => (
                  <span key={chip} className="market-meta-chip">{chip}</span>
                ))}
              </div>
            </div>
            <div className="market-page-scorecard">
              <span className="market-page-scorecard-label">Macro score</span>
              <strong>{formatScore(metrics.macroScore)}</strong>
              <span className="market-page-scorecard-subtitle">
                Dernière mise à jour {metrics.lastUpdate || metrics.timestamp ? new Date(metrics.lastUpdate || metrics.timestamp).toLocaleString('fr-FR') : 'N/A'}
              </span>
            </div>
          </div>
          <div className="market-watch-grid">
            {watchItems.map((item) => (
              <div key={item.label} className="market-watch-card">
                <span className="market-watch-label">{item.label}</span>
                <strong>{item.value}</strong>
                <span className="market-watch-helper">{item.helper}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="market-section market-section-hero">
          <HeroBanner
            regime={metrics.regime}
            marketChange24h={metrics.marketCap24hChange}
            marketLabel="Marché Crypto"
            marketCap={metrics.marketCap}
            sentiment={metrics.sentiment}
            btcDominance={metrics.btcDominance}
            ethDominance={metrics.ethDominance}
            regimeConfidence={metrics.regimeConfidence}
            macroScore={metrics.macroScore}
            lastUpdate={metrics.lastUpdate || metrics.timestamp}
          />
        </section>

        <section className="market-content-grid">
          <div className="market-section">
            <div className="market-section-heading">
              <span>Lecture IA</span>
              <h2>Interprétation guidée du contexte</h2>
            </div>
            <MarketAISummary macroInsight={metrics.macroInsight} />
          </div>

          <aside className="market-section market-focus-panel">
            <div className="market-section-heading">
              <span>Focus immédiat</span>
              <h2>Ce qu’il faut lire en premier</h2>
            </div>
            <div className="market-focus-list">
              <div className="market-focus-item">
                <span className="market-focus-title">Sentiment </span>
                <strong>{sentimentLabel}</strong>
                <p>Thermomètre comportemental immédiat pour éviter de lire la séance à contretemps.</p>
              </div>
              <div className="market-focus-item">
                <span className="market-focus-title">Dominance </span>
                <strong>{formatPercent(metrics.btcDominance, 1)} BTC</strong>
                <p>Mesure si le flux reste sur les leaders ou s’ouvre au reste du marché.</p>
              </div>
              <div className="market-focus-item">
                <span className="market-focus-title">Cadence </span>
                <strong>{displayRiskLabel}</strong>
                <p>Filtre tactique avant de pousser l’exposition, le scanner ou la taille de position.</p>
              </div>
            </div>
          </aside>
        </section>

        <section className="market-section">
          <div className="market-section-heading">
            <span>Métriques structurelles</span>
            <h2>Vue détaillée des piliers macro</h2>
          </div>
          <MacroMetricsGrid
            metrics={{
              btcDom: metrics.btcDominance,
              ethDom: metrics.ethDominance,
              btcVar7d: metrics.btcDom7dVar,
              ethVar7d: metrics.ethDom7dVar,
              sentiment: metrics.sentiment,
              sentimentLabel,
              btcDomHistory: metrics.btcDomHistory,
              sentimentHistory: metrics.sentimentHistory,
              momentum: metrics.momentum,
              momentumVar: metrics.momentumVar,
              momentumHistory: metrics.momentumHistory,
              volatility: metrics.volatility,
              riskOnOff: metrics.riskOnOff,
              breadthUp: metrics.breadthUp,
              breadthTotal: metrics.breadthTotal,
            }}
          />
        </section>
      </div>
    </div>
  );
};

export default MarketIntelligencePage;
